import { useState, useRef, useEffect } from "react";
import { ShieldCheck, ChevronRight, Bell, Lock } from "lucide-react";
import { C } from "./data";
import { Serif, Label, Avatar } from "./ui";
import { getHousehold, getNotices } from "./consumerAuth";
import InstallPrompt from "./InstallPrompt";

const HOLD_MS = 3000;

const countdown = (iso) => {
  const ms = new Date(iso).getTime() - Date.now();
  if (!(ms > 0)) return "a few minutes";
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return h > 0 ? `${h}h ${m}m` : `${m} minutes`;
};

// "Mr R Daniels" -> "Mr Daniels", "Jasmine Daniels" -> "Jasmine", "R Daniels" -> "R Daniels".
const TITLES = /^(mr|mrs|ms|miss|mnr|mev|mej|dr|prof|rev|adv)\.?$/i;
export const greetingName = (name) => {
  const parts = (name || "").trim().split(/\s*(?:&|\band\b)\s*/i)[0].split(/\s+/).filter(Boolean);
  if (!parts.length) return "";
  const title = TITLES.test(parts[0]) ? parts.shift().replace(/\.$/, "") : "";
  if (!parts.length) return title;
  const isInitial = (p) => { const t = p.replace(/\./g, ""); return t.length <= 1 || /^[A-Z]{1,3}$/.test(t); };
  if (!isInitial(parts[0])) return parts[0];
  const surname = parts.filter((p) => !isInitial(p)).join(" ") || parts[parts.length - 1];
  return title ? `${title} ${surname}` : parts.join(" ");
};

export default function Home({ nav, onPanic, user, securityActive = true, onUpgrade, onSubscribe, subscription, fromPrice, debitOrderNeeded = false, onDebitOrder }) {
  const displayName = greetingName(user?.name);
  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  const [hh, setHh] = useState(null);
  useEffect(() => { getHousehold().then(setHh).catch((e) => console.error("household load failed", e)); }, []);
  const previewMembers = hh ? hh.dependents.map((d) => ({ name: d.full_name, role: d.role || "Dependent", activated: d.activated })) : [];
  const [progress, setProgress] = useState(0);
  const timer = useRef(null);
  const fired = useRef(false);
  const start = () => {
    fired.current = false;
    const t0 = Date.now();
    timer.current = setInterval(() => {
      const p = Math.min(1, (Date.now() - t0) / HOLD_MS);
      setProgress(p);
      if (p >= 1 && !fired.current) { fired.current = true; clearInterval(timer.current); onPanic(); }
    }, 30);
  };
  const stop = () => { clearInterval(timer.current); setProgress(0); };
  useEffect(() => () => clearInterval(timer.current), []);

  const [unread, setUnread] = useState(0);
  useEffect(() => { getNotices().then((d) => setUnread((d?.items || []).filter((n) => n.unread).length)).catch(() => {}); }, []);

  return (
    <div style={{ padding: "24px 22px 30px" }} data-testid="consumer-home">
      <InstallPrompt />
      {debitOrderNeeded && (
        <button onClick={onDebitOrder} data-testid="do-banner"
          style={{ width: "100%", textAlign: "left", display: "flex", alignItems: "center", gap: 12, background: C.red, color: "#fff", border: 0, borderRadius: 14, padding: "14px 16px", marginBottom: 18, cursor: "pointer" }}>
          <div style={{ width: 38, height: 38, borderRadius: 10, background: "rgba(255,255,255,0.18)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <ShieldCheck size={20} color="#fff" />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14.5, fontWeight: 800 }}>Confirm your debit order</div>
            <div style={{ fontSize: 12.5, opacity: 0.92, marginTop: 1 }}>A quick authority keeps your cover running. Tap to complete.</div>
          </div>
          <ChevronRight size={20} color="#fff" />
        </button>
      )}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 22 }}>
        <div>
          {displayName ? (
            <>
              <Label>{greeting}</Label>
              <Serif size={28} style={{ marginTop: 4 }}>{displayName}.</Serif>
            </>
          ) : (
            <Serif size={28}>{greeting}.</Serif>
          )}
        </div>
        <button onClick={() => nav("notices")} data-testid="home-bell" style={{ position: "relative", background: "#fff", border: "1px solid rgba(26,30,36,0.1)", borderRadius: 10, padding: 9, cursor: "pointer" }}>
          <Bell size={18} color={C.ink} />
          {unread > 0 && <span style={{ position: "absolute", top: -5, right: -5, background: C.red, color: "#fff", fontSize: 10, fontWeight: 700, width: 18, height: 18, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>{unread}</span>}
        </button>
      </div>

      <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "7px 13px", background: securityActive ? `${C.green}18` : `${C.amber}1f`, color: securityActive ? C.green : C.amber, borderRadius: 999, fontSize: 12, fontWeight: 600, marginBottom: 28 }} data-testid="home-status">
        <span style={{ width: 8, height: 8, background: securityActive ? C.green : C.amber, borderRadius: "50%" }} /> {securityActive ? "System active · GPS locked" : "Family tracking active · Emergency response off"}
      </div>

      {securityActive ? (
        <>
          <button data-testid="home-panic-button" onMouseDown={start} onMouseUp={stop} onMouseLeave={stop} onTouchStart={start} onTouchEnd={stop}
            style={{ position: "relative", width: "100%", padding: "46px 20px", background: C.red, color: "#fff", border: 0, borderRadius: 18, fontSize: 26, fontWeight: 800, letterSpacing: "0.04em", cursor: "pointer", boxShadow: `0 14px 42px ${C.red}55`, overflow: "hidden", userSelect: "none" }}>
            <span style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: `${progress * 100}%`, background: "rgba(0,0,0,0.22)", transition: "width 0.03s linear" }} />
            <span style={{ position: "relative" }}>{progress > 0 ? "KEEP HOLDING…" : "HOLD TO PANIC"}</span>
          </button>
          <div style={{ fontSize: 12, color: C.slate, textAlign: "center", margin: "12px 0 24px" }}>Press and hold 3 seconds to arm · location is sent instantly</div>
        </>
      ) : (
        <>
          <div data-testid="home-panic-locked"
            style={{ position: "relative", width: "100%", padding: "40px 20px", background: "#E7E4DD", color: C.slate, border: `1px dashed ${C.slate}55`, borderRadius: 18, fontSize: 22, fontWeight: 800, letterSpacing: "0.03em", textAlign: "center", userSelect: "none", display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
            <Lock size={26} color={C.slate} />
            <span>EMERGENCY RESPONSE OFF</span>
          </div>
          <div style={{ background: "#fff", border: `1px solid ${C.amber}44`, borderRadius: 14, padding: 16, margin: "14px 0 24px" }} data-testid="home-upgrade-card">
            {subscription?.status === "suspended" ? (
              <>
                <div style={{ fontSize: 14, fontWeight: 700, color: C.ink, marginBottom: 4 }}>Your cover is on hold</div>
                <div style={{ fontSize: 12.5, color: C.slate, lineHeight: 1.5 }} data-testid="home-cover-suspended">
                  Your {subscription.plan_label} package has been suspended. Please contact ResQ1 or your broker to have it reinstated — family tracking stays on in the meantime.
                </div>
              </>
            ) : subscription?.status === "signed" ? (
              <>
                <div style={{ fontSize: 14, fontWeight: 700, color: C.ink, marginBottom: 4 }}>You're signed up — cover is on its way</div>
                <div style={{ fontSize: 12.5, color: C.slate, lineHeight: 1.5 }} data-testid="home-activation-countdown">
                  Your {subscription.plan_label} package is signed. Your panic button switches on in <b style={{ color: C.ink }}>{countdown(subscription.activates_at)}</b>.
                  Your first debit of R{subscription.price} goes off on {String(subscription.first_debit_date || "").slice(0, 7)}-01.
                </div>
              </>
            ) : (
              <>
                <div style={{ fontSize: 14, fontWeight: 700, color: C.ink, marginBottom: 4 }}>
                  {subscription?.status === "pending" ? "Finish your sign-up" : "Activate emergency response"}
                </div>
                <div style={{ fontSize: 12.5, color: C.slate, lineHeight: 1.5, marginBottom: 12 }}>
                  Family tracking stays free. Add a Security Package to unlock the panic button and get armed response dispatched to your location in an emergency.
                </div>
                <button onClick={onSubscribe || onUpgrade} data-testid="home-upgrade-button"
                  style={{ width: "100%", padding: 13, background: C.red, color: "#fff", border: 0, borderRadius: 12, fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
                  {subscription?.status === "pending" ? "Finish sign-up" : `Activate emergency response${fromPrice ? ` · from R${fromPrice}pm` : ""}`}
                </button>
              </>
            )}
          </div>
        </>
      )}

      <button onClick={() => nav("selftest")} data-testid="home-selftest"
        style={{ width: "100%", padding: 14, background: "transparent", color: C.navy, border: `1.5px solid ${C.navy}`, borderRadius: 12, fontSize: 14, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginBottom: 28 }}>
        <ShieldCheck size={17} /> Test my safety net
      </button>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <Label>{hh ? `My household · ${hh.dependents.length + 1} member${hh.dependents.length === 0 ? "" : "s"}` : "My household"}</Label>
        <button onClick={() => nav("dependents")} data-testid="home-manage-household" style={{ background: "none", border: 0, color: C.red, fontSize: 12, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center" }}>
          Manage <ChevronRight size={14} />
        </button>
      </div>
      {previewMembers.map((m) => (
        <div key={m.name} onClick={() => nav("dependents")} style={{ display: "flex", alignItems: "center", gap: 12, padding: 11, background: "#fff", border: "1px solid rgba(26,30,36,0.07)", borderRadius: 10, marginBottom: 7, cursor: "pointer" }} data-testid={`home-dep-${m.name.split(" ")[0].toLowerCase()}`}>
          <Avatar name={m.name} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: C.ink }}>{m.name}</div>
            <div style={{ fontSize: 11, color: C.slate }}>{m.role}</div>
          </div>
          <div style={{ fontSize: 11, fontWeight: 600, color: m.activated ? C.green : C.amber }}>
            {m.activated ? "● active" : "● pending"}
          </div>
        </div>
      ))}
      {hh && previewMembers.length === 0 && (
        <div style={{ padding: "11px 13px", background: C.cream, borderRadius: 10, fontSize: 12, color: C.slate }} data-testid="home-household-empty">
          No dependents linked yet — invite your family so they carry the panic button too.
        </div>
      )}
    </div>
  );
}
